import { matchPath } from 'react-router-dom';

// HELP_PAGE_MAP keys are route patterns matching the routes registered in
// App.tsx. Patterns may include :slug or :id placeholders; matching is done
// against the current pathname via matchPath() from react-router-dom.
// Values are arrays of help-article slugs that the contextual drawer should
// surface when the user is on the matching route. Order = priority order in
// the drawer body (first listed is most relevant).
//
// The map is hand-maintained. Every article slug listed here must eventually
// exist in HELP_ARTICLES; until it does, the helper silently filters it out
// so K0 ships without crashing.
export const HELP_PAGE_MAP: Record<string, string[]> = {
  // Site-scoped routes (most specific first):
  '/sites/:slug/brainstorm': [
    'brainstorm',
    'brainstorm-suggestions',
    'approving-briefs',
    'brief-lifecycle',
    'understanding-the-pipeline',
    'search-filter-sort',
    'keyboard-shortcuts-quick',
    'troubleshooting',
  ],
  '/sites/:slug/pipeline': [
    'pipeline',
    'canceling-pipeline',
    'pipeline-states',
    'understanding-the-pipeline',
    'ai-pipeline-mental-model',
    'search-filter-sort',
    'troubleshooting',
  ],
  '/sites/:slug/drafts/:id': [
    'unified-editor',
    'shorthand-components',
    'shorthand-syntax',
    'component-reference',
    'illustrations',
    'images',
    'interlinks',
    'factual-flags',
    'authority-citations',
    'annotations',
    'annotation-lifecycle',
    'revisions',
    'preview-modal',
    'visibility-toggles',
    'focus-mode',
    'regulatory-disclaimers',
    'article-lifecycle',
    'component-system',
    'troubleshooting',
  ],
  '/sites/:slug/drafts': [
    'unified-editor',
    'first-article-end-to-end',
    'search-filter-sort',
    'archiving-deleting',
    'troubleshooting',
  ],
  '/sites/:slug/articles': [
    'publishing',
    'unpublishing',
    'article-lifecycle',
    'archiving-deleting',
    'bulk-operations',
    'search-filter-sort',
    'export',
    'publishing-infrastructure',
    'troubleshooting',
  ],
  '/sites/:slug/pages': ['managing-site-pages', 'component-reference', 'publishing'],
  '/sites/:slug/pages/new': ['managing-site-pages', 'shorthand-components', 'component-reference', 'publishing'],
  '/sites/:slug/pages/:pageId': ['managing-site-pages', 'shorthand-components', 'component-reference', 'publishing'],
  '/sites/:slug/analytics': ['analytics', 'export', 'settings-api-keys', 'troubleshooting'],
  '/sites/:slug/content-graph': ['interlinks', 'authority-sources', 'troubleshooting'],
  '/sites/:slug/alerts': ['alerts', 'error-codes', 'search-filter-sort', 'troubleshooting'],
  '/sites/:slug/prompts': [
    'prompts-overview',
    'ai-prompt-generation',
    'prompt-testing',
    'prompt-syncing',
    'prompt-placeholders',
    'prompt-architecture',
    'ai-pipeline-mental-model',
    'troubleshooting',
  ],
  '/sites/:slug/prompts/:type': [
    'prompts-overview',
    'ai-prompt-generation',
    'prompt-testing',
    'prompt-syncing',
    'prompt-placeholders',
    'prompt-architecture',
    'ai-pipeline-mental-model',
    'troubleshooting',
  ],
  '/sites/:slug/authority-sources': ['authority-sources', 'troubleshooting'],
  '/sites/:slug': [
    'site-management',
    'site-hub',
    'multi-site-navigation',
    'theme-customization',
    'theme-variables',
    'theming-system',
    'domain-setup',
    'component-packs',
    'site-context-schema',
    'dashboard-layout',
    'troubleshooting',
  ],

  // Global routes:
  '/sites': [
    'site-management',
    'site-creation-wizard',
    'first-site',
    'multi-site-navigation',
    'dashboard-layout',
    'troubleshooting',
  ],
  '/prompts': [
    'prompts-overview',
    'prompt-architecture',
    'ai-pipeline-mental-model',
    'ai-prompt-generation',
    'prompt-syncing',
    'prompt-placeholders',
    'troubleshooting',
  ],
  '/prompts/:type': [
    'prompts-overview',
    'prompt-architecture',
    'ai-pipeline-mental-model',
    'ai-prompt-generation',
    'prompt-syncing',
    'prompt-placeholders',
    'troubleshooting',
  ],
  '/alerts': ['alerts', 'error-codes', 'troubleshooting'],
  '/settings': ['settings-api-keys', 'api-key-permissions', 'error-codes', 'backups', 'troubleshooting'],
  '/setup': ['platform-overview', 'aeo-explained', 'first-site', 'troubleshooting'],
  '/help': [
    'platform-overview',
    'aeo-explained',
    'first-article-end-to-end',
    'keyboard-shortcuts-full',
    'troubleshooting',
  ],
  '/help/:slug': [],
};

// helpSlugsForPath resolves the current pathname to a list of help-article
// slugs. Matching is most-specific-first: longer route patterns win over
// shorter ones. Slugs that don't yet exist in HELP_ARTICLES are filtered out
// so the drawer never references a missing article.
export function helpSlugsForPath(pathname: string, knownSlugs: Set<string>): string[] {
  let bestMatch: { pattern: string; specificity: number } | null = null;
  for (const pattern of Object.keys(HELP_PAGE_MAP)) {
    const result = matchPath({ path: pattern, end: true }, pathname);
    if (!result) continue;
    const specificity = pattern.split('/').length * 100 - countParams(pattern);
    if (!bestMatch || specificity > bestMatch.specificity) {
      bestMatch = { pattern, specificity };
    }
  }
  if (!bestMatch) return [];
  const raw = HELP_PAGE_MAP[bestMatch.pattern] ?? [];
  return raw.filter((slug) => knownSlugs.has(slug));
}

function countParams(pattern: string): number {
  return (pattern.match(/:[A-Za-z]+/g) ?? []).length;
}
